import { useCallback, useEffect, useMemo, useState } from 'react';
import { CountySelectComponent } from '~/components/dropdowns/county/county-select.component';
import { HouseholdOccupancySelectComponent } from '~/components/dropdowns/house-hold-occupancy/household-occupancy-select.component';
import { LmiQualifyingProgramSelectComponent } from '~/components/dropdowns/lmi-qualifying-program/lmi-qualifying-program-select.component';
import { Container } from '~/components/layout';
import { ReviewContext } from '~/machines/review-form';
import { SelfAttestationData, VnmTermsContext } from '~/screens/vnm-terms';
import { CountyIncomeLimits } from '~/screens/vnm-terms/self-attestation-form/county-income-limits';

interface Props {
  context: VnmTermsContext | ReviewContext;
  onFormUpdate: (data: SelfAttestationData) => void;
}

const MAX_HOUSEHOLD_OCCUPANCY_LIMIT = 8;
const defaultCounties = Object.freeze([
  'Atlantic County',
  'Bergen County',
  'Burlington County',
  'Camden County',
  'Cape may County',
  'Cumberland County',
  'Essex County',
  'Gloucester County',
  'Hudson County',
  'Hunterdon County',
  'Mercer County',
  'Middlesex County',
  'Monmouth County',
  'Morris County',
  'Ocean County',
  'Passaic County',
  'Salem County',
  'Somerset County',
  'Sussex County',
  'Union County',
  'Warren County',
]);

export const SelfAttestationForm = ({ context, onFormUpdate }: Props) => {
  const { zipCode, id: userId } = context.funnelPacket.user;

  const houseOccupancyOptions = useMemo(() => {
    const options: Array<{
      disabled: boolean;
      selected: boolean;
      text: string;
      value: number;
    }> = [];
    for (let i = 1; i <= MAX_HOUSEHOLD_OCCUPANCY_LIMIT; i++) {
      options[i] = {
        disabled: false,
        selected: false,
        text: `${i > 1 ? `${i} Persons` : `${i} Person`}`,
        value: i,
      };
    }
    return options;
  }, []);
  const lmiProgramOptions = useMemo(() => {
    const assistancePrograms = {
      liheap: 'Low Income Home Energy Assistance Program (LIHEAP)',
      usf: 'Universal Service Fund (USF)',
      comfortPartners: 'Comfort Partners',
      lifelineUtilityAssistanceProgram: 'Lifeline utility assistance program',
      page: 'Payment Assistance for Gas and Electric (PAGE)',
      section8HousingChoiceVoucherProgram: 'Section 8 Housing Choice Voucher Program',
      snap: 'Supplemental Nutrition Assistance Program (SNAP)',
      lifelineProgram: 'Lifeline program (Universal Service Administrative Company)',
      medicaid: 'Medicaid',
      ssiOrSsdi:
        'Supplemental Security Income (SSI) or Social Security Disability Insurance (SSDI)',
      wic: 'Special Supplemental Nutrition Program for Women, Infants, and Children (WIC)',
      tanf: 'Temporary Assistance for Needy Families (TANF)',
      lihwap: 'Low-Income Household Water Assistance Program (LIHWAP)',
    };
    return Object.keys(assistancePrograms).map(programKey => {
      return {
        disabled: false,
        selected: false,
        text: assistancePrograms[programKey],
        value: programKey,
      };
    });
  }, []);

  const [formData, setFormData] = useState({
    county: '',
    houseOccupancy: '',
    lmiProgram: '',
  });

  const [selfAttestationData, setSelfAttestationData] = useState({
    county: '',
    houseOccupancy: '',
    lmiProgram: lmiProgramOptions[0].text,
    houseIncome: '',
  });

  const handleOnChange = useCallback((fieldName: keyof typeof formData, value) => {
    setFormData(prevState => {
      return {
        ...prevState,
        [fieldName]: value,
      };
    });
  }, []);

  useEffect(() => {
    const lmiProgram = lmiProgramOptions.find(option => option.value === formData.lmiProgram)?.text;
    const houseOccupancy = formData.houseOccupancy
      ? String(houseOccupancyOptions[Number(formData.houseOccupancy)].value)
      : '';
    const houseIncome =
      formData.houseOccupancy &&
      CountyIncomeLimits.getIncomeLimits(
        formData.county.toLowerCase().split('county')[0].trim(),
        Number(formData.houseOccupancy) - 1,
      );
    setSelfAttestationData({
      county: formData.county,
      houseOccupancy,
      lmiProgram: lmiProgram || '',
      houseIncome,
    });
  }, [formData]);

  useEffect(() => {
    onFormUpdate(selfAttestationData);
  }, [selfAttestationData]);
  return (
    <Container addInputMargin>
      <CountySelectComponent
        zipCode={zipCode!}
        defaultCounties={defaultCounties}
        userId={userId}
        onChange={value => {
          handleOnChange('county', value);
        }}
        value={formData.county}
      />
      <HouseholdOccupancySelectComponent
        onChange={value => handleOnChange('houseOccupancy', houseOccupancyOptions[value].value)}
        options={houseOccupancyOptions}
        value={formData.houseOccupancy}
      />
      <LmiQualifyingProgramSelectComponent
        onChange={value => {
          if (value === '') {
            handleOnChange('lmiProgram', '');
            return;
          }
          handleOnChange('lmiProgram', value);
        }}
        options={lmiProgramOptions}
        value={formData.lmiProgram}
      />
    </Container>
  );
};
